'use client';
import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from '@/lib/utils';
import { useLocale, useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMediaQuery } from '@/hooks/use-media-query';
import AnimatedCard from '@/components/ui/animated-card';
import BorderWrapper from '@/components/ui/border-wrapper';
import { motion } from 'motion/react';
import { toast } from 'sonner';

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: 'easeInOut', repeat: Infinity },
  },
} as const;

const SERVICE_KEYS = [
  'webDevelopment',
  'mobileApp',
  'uiUxDesign',
  'branding',
  'apiIntegration',
  'eCommerce',
  'cmsSetup',
  'devOpsCloud',
  'legal',
] as const;

const BUDGET_KEYS = [
  'budget10k',
  'budget25k',
  'budget50k',
  'budget100k',
  'budgetAbove',
] as const;

interface GetInTouchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ContactFormData = {
  name: string;
  email: string;
  service?: string[];
  budget?: string;
  description: string;
};

const GetInTouch = ({ open, onOpenChange }: GetInTouchProps) => {
  const t = useTranslations('contact');
  const tA11y = useTranslations('accessibility');
  const locale = useLocale();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const translatedSchema = z.object({
    name: z.string().min(2, t('nameError')),
    email: z.string().email(t('emailError')),
    service: z.array(z.string()).optional(),
    budget: z.string().optional(),
    description: z.string().min(10, t('descriptionError')),
  });

  

  useEffect(() => {
    const lenis = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).__lenis as { stop: () => void; start: () => void } | undefined : undefined;
    if (open) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
    return () => {
      lenis?.start();
    };
  }, [open]);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(translatedSchema),
    defaultValues: {
      name: '',
      email: '',
      service: [],
      budget: '',
      description: '',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          locale,
          serviceKeys: data.service ?? [],
          services: (data.service ?? []).map((key) => t(key as typeof SERVICE_KEYS[number])),
          budget: data.budget ? t(data.budget as typeof BUDGET_KEYS[number]) : '',
          description: data.description,
        }),
      });
      if (!res.ok) throw new Error('Failed to send');
      form.reset();
      onOpenChange(false);
      toast.success(t('successTitle'), { description: t('successDescription') });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(t('errorTitle'), { description: t('errorDescription') });
    }
  };

  const selectedServices = form.watch('service') ?? [];
  const isLegalSelected = selectedServices.includes('legal');

  const [showScrollHint, setShowScrollHint] = React.useState(true);
  const [showDesktopScrollHint, setShowDesktopScrollHint] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const desktopScrollRef = React.useRef<HTMLDivElement>(null);

  const handleDrawerScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setShowScrollHint(!atBottom);
  };

  const checkDesktopOverflow = () => {
    const el = desktopScrollRef.current;
    if (!el) return;
    const isOverflowing = el.scrollHeight > el.clientHeight + 10;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setShowDesktopScrollHint(isOverflowing && !atBottom);
  };

  const handleDesktopScroll = () => checkDesktopOverflow();

  // Check if desktop form is scrollable on open and resize
  useEffect(() => {
    if (!open) {
      setShowDesktopScrollHint(false);
      return;
    }
    const timer = setTimeout(checkDesktopOverflow, 150);
    window.addEventListener('resize', checkDesktopOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkDesktopOverflow);
    };
  }, [open]);

  const formContent = (
    <Form {...form}>
      <form id="get-in-touch-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-[10px] px-[16px] md:px-[10px] pt-[2px] pb-[24px]">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{t('name')}</FormLabel>
              <FormControl>
                <Input placeholder={t('name')} {...field} />
              </FormControl>
              <FormMessage className="text-white/60" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{t('email')}</FormLabel>
              <FormControl>
                <Input type="email" placeholder={t('email')} {...field} />
              </FormControl>
              <FormMessage className="text-white/60" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="rounded-[10px] lg:rounded-[15px] border border-[#353353] p-[16px] lg:p-[20px]">
                  <h6 className="m-0 text-[15px] lg:text-[16px] text-foreground/90 mb-[12px]">{t('serviceRequired')}</h6>
                  <div className="flex flex-wrap gap-[8px]">
                    {SERVICE_KEYS.map((key) => {
                      const label = t(key);
                      const current = field.value ?? [];
                      const selected = current.includes(key);
                      const isLegalSelected = current.includes('legal');
                      const disabled = key === 'legal' ? false : isLegalSelected;
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            if (key === 'legal') {
                              field.onChange(selected ? [] : ['legal']);
                              if (!selected) form.setValue('budget', '');
                            } else {
                              field.onChange(
                                selected ? current.filter((v) => v !== key) : [...current, key]
                              );
                            }
                          }}
                          className={cn(
                            "px-[14px] py-[7px] rounded-full text-[13px] leading-[1] border transition-all duration-200 cursor-pointer",
                            selected
                              ? "bg-[#4a3290] border-[#4a3290] text-white"
                              : "bg-transparent border-[#353353] text-foreground/90 hover:border-theme/50 hover:text-foreground",
                            disabled && "opacity-30 cursor-not-allowed hover:border-[#353353] hover:text-foreground/90"
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-white/60" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="budget"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className={cn(
                  "rounded-[10px] lg:rounded-[15px] border border-[#353353] p-[16px] lg:p-[20px] transition-opacity duration-200",
                  isLegalSelected && "opacity-30"
                )}>
                  <h6 className="m-0 text-[15px] lg:text-[16px] text-foreground/90 mb-[12px]">{t('budgetRange')}</h6>
                  <div className="flex flex-wrap gap-[8px]">
                    {BUDGET_KEYS.map((key) => {
                      const label = t(key);
                      const selected = field.value === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={isLegalSelected}
                          onClick={() => field.onChange(selected ? '' : key)}
                          className={cn(
                            "px-[14px] py-[7px] rounded-full text-[13px] leading-[1] border transition-all duration-200 cursor-pointer",
                            selected
                              ? "bg-[#4a3290] border-[#4a3290] text-white"
                              : "bg-transparent border-[#353353] text-foreground/90 hover:border-theme/50 hover:text-foreground",
                            isLegalSelected && "cursor-not-allowed hover:border-[#353353] hover:text-foreground/90"
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-white/60" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{t('projectDescription')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={isLegalSelected ? t('legalDescription') : t('projectDescription')}
                  className="min-h-[90px] lg:min-h-[120px] focus:min-h-[180px] transition-all duration-300"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-white/60" />
            </FormItem>
          )}
        />

      </form>
    </Form>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[660px] p-0 bg-transparent border-none" showCloseButton={false}>
          <AnimatedCard className="rounded-[25px]" circleClassName="w-[220px] h-[220px] blur-[100px]">
            <BorderWrapper wrapperClass="rounded-[25px]" className="rounded-[25px] bg-gradient-to-b from-[#121124] to-[#0e0821] px-[10px] py-[20px] lg:p-[40px] relative overflow-hidden">
              <div
                className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                />
              <div
                className="absolute bottom-0 left-0 w-[150px] h-[150px] bg-[#622aad]/10 rounded-full blur-[70px] pointer-events-none animate-shape-pulse"
                />
              <Button
                variant="secondary"
                className="absolute top-[14px] right-[14px] w-[36px] h-[36px] p-0 shrink-0 rounded-full cursor-pointer z-20"
                aria-label={tA11y('closeDialog')}
                onClick={() => onOpenChange(false)}
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </Button>
              <div className="p-[10px] pb-4 relative z-10">
                <DialogHeader className='text-start'>
                  <DialogTitle className="text-[44px] lg:text-[48px] leading-[1] font-semibold">
                    {t('title')}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {t('subtitle')}
                  </DialogDescription>
                  <p className="text-[14px] lg:text-[16px] font-normal text-foreground/90">
                    {t('subtitle')}
                  </p>
                </DialogHeader>
              </div>
              {/* Scrollable form area */}
              <div className="relative z-10">
                <div
                  ref={desktopScrollRef}
                  data-lenis-prevent
                  className="max-h-[60vh] overflow-y-auto scrollbar-hide"
                  onScroll={handleDesktopScroll}
                >
                  {formContent}
                </div>

                {/* Scroll hint */}
                {showDesktopScrollHint && (
                  <div className="absolute bottom-0 left-[10%] right-[10%] h-[60px] pointer-events-none flex items-end justify-center pb-[8px] z-10" style={{ background: 'radial-gradient(ellipse at center bottom, rgba(14,8,33,0.9) 0%, rgba(14,8,33,0.5) 50%, transparent 100%)' }}>
                    <motion.div
                      animate={{ y: [0, 4, 0] }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                      className="flex items-center gap-[6px] bg-white/8 backdrop-blur-sm border border-white/20 rounded-full px-[14px] py-[6px]"
                    >
                      <span className="text-[11px] font-light text-white/90 leading-none">{t('scrollForMore')}</span>
                      <svg width="11" height="11" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6L9 12L15 6" stroke="white" strokeOpacity="0.9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.div>
                  </div>
                )}
              </div>

              {/* Send button — always visible */}
              <div className="relative z-10 px-[10px] pt-[16px] pb-[10px] flex flex-col items-end gap-[10px]">
                <Button
                  type="submit"
                  form="get-in-touch-form"
                  variant="theme"
                  className="h-[48px] rounded-[14px] px-[28px] text-[14px] md:text-[16px] shadow-none min-w-[150px] font-light cursor-pointer"
                  disabled={form.formState.isSubmitting}
                  aria-busy={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? t('sending') : t('sendMessage')}
                </Button>
                <p className="text-[12px] text-foreground/40 font-light">{t('responseTime')}</p>
              </div>
            </BorderWrapper>
          </AnimatedCard>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh] flex flex-col bg-gradient-to-b from-[#121124] to-[#0e0821] border-white/10">
        <div
          className="absolute top-0 right-0 w-[180px] h-[180px] bg-theme/10 rounded-full blur-[70px] pointer-events-none animate-shape-pulse"
          />
        <DrawerHeader className='text-center pt-[20px] pb-[16px] px-[16px] relative z-10'>
          <DrawerTitle className="text-[28px] leading-[1] font-semibold">
            {t('title')}
          </DrawerTitle>
          <p className="text-[14px] font-normal text-foreground/90 mt-[6px]">
            {t('subtitle')}
          </p>
        </DrawerHeader>

        <div
          ref={scrollRef}
          data-lenis-prevent
          className="flex-1 overflow-y-auto scrollbar-hide"
          onScroll={handleDrawerScroll}
          onFocus={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
              setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350);
            }
          }}
        >
          {formContent}
        </div>

        {/* Scroll hint — sits just above the sticky footer, fades out at bottom */}
        {showScrollHint && (
          <div className="absolute bottom-[100px] left-0 right-0 h-[70px] pointer-events-none flex flex-col items-center justify-end pb-[10px] bg-gradient-to-t from-[#0e0821] to-transparent z-10">
            <motion.div
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              className="flex items-center gap-[6px] bg-white/8 backdrop-blur-sm border border-white/20 rounded-full px-[14px] py-[6px]"
            >
              <span className="text-[11px] font-light text-white/90 leading-none">{t('scrollForMore')}</span>
              <svg width="11" height="11" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6L9 12L15 6" stroke="white" strokeOpacity="0.9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          </div>
        )}

        {/* Sticky footer */}
        <div className="shrink-0 flex flex-col items-center gap-[8px] px-[16px] py-[16px] pb-[calc(16px+env(safe-area-inset-bottom))] border-t border-white/10 bg-[#0e0821] relative z-10">
          <Button
            type="submit"
            form="get-in-touch-form"
            variant="theme"
            className="w-full h-[48px] rounded-[14px] text-[14px] md:text-[16px] shadow-none font-light cursor-pointer"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? t('sending') : t('sendMessage')}
          </Button>
          <p className="text-[12px] text-foreground/40 font-light">{t('responseTime')}</p>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default GetInTouch;
